import { Pagination } from "antd";
import { apiV2Root } from "consts";
import RecalculateRatingsButton from "features/activity-log/blocks/RecalculateRatingsButton";
import { isValidContentType } from "features/cases/contentType";
import CommentsFeed from "features/comments/blocks/CommentsFeed";
import FeedSwitcher from "features/moderatorial/blocks/FeedSwitcher";
import {
  ContentStatus,
  ContentType,
  isValidContentStatus,
  MODERATORIAL_CASES_QUERY_KEY,
} from "features/moderatorial/types";
import ContentItemsView from "feeds/ContentItemsView";
import SortToggle from "feeds/SortToggle";
import type { SortField, SortDirection } from "feeds/SortToggle.types";
import { useDataQueryParams } from "hooks/useDataQueryParams";
import { useFlag } from "hooks/useFlags";
import { useSearchParam } from "hooks/useSearchParam";
import { useUser } from "hooks/useUser";
import React, { useState } from "react";
import type { Card, PaginatedResponse, Problem, Solution } from "types/basic";

import "./ModeratorialFeed.css";

const ModeratorialFeed: React.FC = () => {
  const { isModerator } = useUser();
  const moderationEnabled = useFlag("moderationEnabled");

  const [rawType, setType] = useSearchParam("type");
  const type = isValidContentType(rawType) ? rawType : "problem";

  const [rawStatus, setStatus] = useSearchParam("status");
  const status = isValidContentStatus(rawStatus) ? rawStatus : "queue";

  const [rawPage, setPage] = useSearchParam("page");
  const page = Number(rawPage) || 1;

  const [sortField, setSortField] = useState<SortField>("dateModified");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [groupByCase, setGroupByCase] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);

  const ordering = `${sortDirection === "desc" ? "-" : ""}${sortField}`;

  const onChangeType = (newType: ContentType) => setType(newType, ["page"]);

  const onChangeStatus = (newStatus: ContentStatus) =>
    setStatus(newStatus, ["page"]);

  const onPageChange = (newPage: number) => setPage(String(newPage));

  const handleSortChange = (
    field: SortField,
    direction: SortDirection,
    newGroupByCase: boolean,
  ) => {
    setSortField(field);
    setSortDirection(direction);
    setGroupByCase(newGroupByCase);
  };

  const isCommentType = type === "comment";

  const casesResponse = useDataQueryParams<
    PaginatedResponse<Problem | Solution | Card>
  >(
    MODERATORIAL_CASES_QUERY_KEY,
    { type, status, page, ordering },
    apiV2Root,
    isCommentType,
  );

  if (!isModerator || !moderationEnabled) {
    return (
      <p>
        Ошибка при загрузке данных. Возможно, у вас недостаточно прав для
        просмотра этого контента.
      </p>
    );
  }

  return (
    <div className="moderatorial-feed">
      <RecalculateRatingsButton />

      <FeedSwitcher
        type={type}
        status={status}
        onChangeType={onChangeType}
        onChangeStatus={onChangeStatus}
      />

      {isCommentType ? (
        <CommentsFeed isModeratorial={true} />
      ) : !casesResponse ? (
        <div>Загрузка...</div>
      ) : (
        <>
          <SortToggle
            onSortChange={handleSortChange}
            initialField={sortField}
            initialDirection={sortDirection}
            initialGroupByCase={groupByCase}
            initialShowList={showList}
            onDisplayModeChange={setShowList}
          />

          <ContentItemsView
            items={casesResponse.results || []}
            solutions={[]}
            showList={showList}
            sortField={sortField}
            fromModeratorialFeed={true}
          />

          {casesResponse.totalPages > 1 && (
            <div className="moderatorial-feed-pagination">
              <Pagination
                current={casesResponse.page}
                pageSize={casesResponse.pageSize}
                total={casesResponse.count}
                onChange={onPageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ModeratorialFeed;
